import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Group, LoadingOverlay, Paper, Title } from "@mantine/core";
import { Client, ClientSettingsInput } from "src/graphql/generated/graphql";
import { handleError } from "src/utils/error";

import { useUpdateClientSettings } from "../../../../hooks/clients";
import {
    ClientSettingsForm,
    ClientSettingsFormProvider,
    parseClientSettingsInput,
    useClientSettingsForm,
} from "../../../ClientSettingsForm";

export interface ClientSettingsProps {
    client: Client;
    isLoading?: boolean;
    onClientUpdate?();
}

export const ClientSettings = ({ client, isLoading, onClientUpdate }: ClientSettingsProps) => {
    const { t } = useTranslation(["common"]);

    const clientSettingsForm = useClientSettingsForm({});

    useEffect(() => {
        if (client) {
            const values = parseClientSettingsInput(client);
            clientSettingsForm.setValues(values);
            clientSettingsForm.resetDirty(values);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(client)]);

    const { updateClientSettings, isUpdatingClientSettings } = useUpdateClientSettings(client?.id);

    const handleSubmit = useCallback(
        async (settings: ClientSettingsInput) => {
            try {
                await updateClientSettings(settings);
                onClientUpdate?.();
            } catch (err) {
                handleError(err, {
                    message: { key: "client_settings_saving_failed", namespace: "common" },
                    context: { clientId: client?.id, ...settings },
                });
            }
        },
        [client?.id, onClientUpdate, updateClientSettings]
    );

    return (
        <ClientSettingsFormProvider form={clientSettingsForm}>
            <Paper
                component="form"
                pos="relative"
                p={36}
                radius={12}
                onSubmit={clientSettingsForm.onSubmit(handleSubmit)}
            >
                <LoadingOverlay visible={isLoading || isUpdatingClientSettings} />
                <Title mb={28} order={3} size={16} weight={600}>
                    {t("client_settings")}
                </Title>
                <ClientSettingsForm />
                <Group mt="xl" position="right">
                    <Button
                        type="submit"
                        size="lg"
                        radius="md"
                        color="primary.main"
                        variant="filled"
                        style={{
                            fontSize: 14,
                        }}
                    >
                        {t("client_settings_save")}
                    </Button>
                </Group>
            </Paper>
        </ClientSettingsFormProvider>
    );
};
